import CreateRecordModal from '../components/CreateRecordModal';
import PermissionGate from '../components/PermissionGate';
import React, { useEffect, useState } from "react";
import axios from 'axios';
import { Alert, Button } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { PageCompanyTable } from "../components/Tables";
import ListingViewToggle from "../components/ListingViewToggle";
import { CompanyForm } from "./CreateCompany";
import { CompanyEditForm } from "./EditCompany";
import { hasUserPermission } from "../utils/userPermissions";
import { Link } from 'react-router-dom';
import { API_URL } from '../const';

export default () => {
  const [viewMode, setViewMode] = useState("grid");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");
  const [canCreateCompany, setCanCreateCompany] = useState(false);

  const loggedInAdmin = JSON.parse(sessionStorage.getItem('quoteadmin'));
  const isLoggedInSuperOrAdmin = loggedInAdmin?.role_name === "Admin" || loggedInAdmin?.role_name === "Super Admin" || loggedInAdmin?.role_name === "Internal Sales";

  useEffect(() => {
    let active = true;
    const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
    axios.get(`${API_URL}auth/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!active) return;
        const context = data.data;
        setCanCreateCompany(Boolean(data.status && context && (
          context.is_super_admin ||
          context.allow_multiple_companies ||
          (Array.isArray(context.companies) && context.companies.length === 0)
        )));
      })
      .catch(() => { if (active) setCanCreateCompany(false); });
    return () => { active = false; };
  }, [refreshKey]);

  const closeCreateModal = () => {
    if (!isCreating) setShowCreateModal(false);
  };

  const handleCompanyCreated = (result) => {
    setShowCreateModal(false);
    setCanCreateCompany(false);
    setSuccessMessage(result.message || 'Company created successfully.');
    setRefreshKey(value => value + 1);
  };

  const openEditModal = id => {
    if (!hasUserPermission("companies", "edit")) return;
    setSuccessMessage("");
    setEditingCompanyId(id);
  };

  const closeEditModal = () => {
    if (!isUpdating) setEditingCompanyId(null);
  };

  const handleCompanyUpdated = result => {
    setEditingCompanyId(null);
    setSuccessMessage(result.message || "Company updated successfully.");
    setRefreshKey(value => value + 1);
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-3">
        <div className="my-auto">
          <h2 className="page-breadcrumb">Company</h2>
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2">
          <ListingViewToggle viewMode={viewMode} onChange={setViewMode} />
          {canCreateCompany && (
            <PermissionGate module="companies" action="add"><Button
              variant="primary"
              className="btn-quote-blue d-inline-flex align-items-center py-2 px-3 box-shedow-none outline-none"
              onClick={() => { setSuccessMessage(""); setShowCreateModal(true); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-plus-circle me-1"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
              Create Company
            </Button></PermissionGate>
          )}

          {isLoggedInSuperOrAdmin && (
            <Link to="/cdeleted">
              <Button variant="outline-secondary" className="ms-2 py-2 px-3">
                Deleted Records
              </Button>
            </Link>
          )}
        </div>
      </div>

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage("")} className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{successMessage}</span>
        </Alert>
      )}

      <div className="my-auto">
        <PageCompanyTable viewMode={viewMode} refreshKey={refreshKey} onEditCompany={openEditModal} />
      </div>

      <CreateRecordModal
        show={showCreateModal}
        onHide={closeCreateModal}
        isSubmitting={isCreating}
        titleId="create-company-title"
        title="Create Company"
        description="Add a company and its business address."
        icon={faBuilding}
        size="lg"
      >
        {showCreateModal && (
          <CompanyForm
            onSuccess={handleCompanyCreated}
            onCancel={closeCreateModal}
            onSubmittingChange={setIsCreating}
          />
        )}
      </CreateRecordModal>

      <CreateRecordModal
        show={editingCompanyId !== null}
        onHide={closeEditModal}
        isSubmitting={isUpdating}
        titleId="edit-company-title"
        title="Edit Company"
        description="Update the company name and business address."
        icon={faBuilding}
        size="lg"
      >
        {editingCompanyId !== null && (
          <CompanyEditForm
            key={editingCompanyId}
            companyId={editingCompanyId}
            onSuccess={handleCompanyUpdated}
            onCancel={closeEditModal}
            onSubmittingChange={setIsUpdating}
          />
        )}
      </CreateRecordModal>
    </>
  );
};
